import { useCallback, useState } from 'react';
import { storageService } from '@/services/storage.service';
import { FileUploadResponse, UploadProgress } from '@/types/storage.types';
import TooltipArrow from '@/components/ui/TooltipArrow';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { Check, CheckCircle2, Eye, Pause, Play, Trash2, Upload, Volume2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import FormField from '@/components/common/form-fields/FormField';

export interface MediaUploaderProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  currentUrl?: string;
  onUploadComplete: (url: string) => void;
  onUploadError?: (error: Error) => void;
  onRemove?: () => void;
  accept?: 'image/*' | 'audio/*' | 'video/*' | 'image/*,audio/*' | 'image/*,video/*';
  mediaType?: 'image' | 'audio' | 'video' | 'any';
  maxSize?: number; // in MB
  className?: string;
}

/**
 * Enhanced media uploader with preview capabilities for images, audio, and video
 * Extends FileUploadField with media-specific features like audio player and video preview
 */
export default function MediaUploader({
  label,
  description,
  error,
  required = false,
  currentUrl,
  onUploadComplete,
  onUploadError,
  onRemove,
  accept = 'image/*',
  mediaType = 'image',
  maxSize = 10,
  className = '',
}: MediaUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [maxSize, mediaType]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    // Validate file
    const validation = storageService.validateFile(file, { maxSizeMB: maxSize });
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid file');
      onUploadError?.(new Error(validation.error || 'Invalid file'));
      return;
    }

    // Validate file type
    if (mediaType === 'image' && !file.type.startsWith('image/')) {
      const errorMsg = 'Please select an image file (PNG, JPG, GIF, WebP)';
      toast.error(errorMsg);
      onUploadError?.(new Error(errorMsg));
      return;
    }

    if (mediaType === 'audio' && !file.type.startsWith('audio/')) {
      const errorMsg = 'Please select an audio file (MP3, WAV)';
      toast.error(errorMsg);
      onUploadError?.(new Error(errorMsg));
      return;
    }

    if (mediaType === 'video' && !file.type.startsWith('video/')) {
      const errorMsg = 'Please select a video file (MP4, WebM)';
      toast.error(errorMsg);
      onUploadError?.(new Error(errorMsg));
      return;
    }

    // Create preview
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
    }

    setFileName(file.name);
    setUploadComplete(false);
    setUploading(true);
    setUploadProgress(0);

    try {
      const response: FileUploadResponse = await storageService.uploadFile(file, {
        onProgress: (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
        },
        metadata: { type: mediaType },
      });

      // Show completion state
      setUploadProgress(100);
      setUploadComplete(true);

      // Success - update with S3 URL after brief delay
      setTimeout(() => {
        onUploadComplete(response.url);
        toast.success('Media uploaded successfully!');
        setPreviewUrl(null);
        setUploading(false);
        setUploadProgress(0);
        setUploadComplete(false);
      }, 800);
    } catch (error: any) {
      console.error('Upload failed:', error);
      const errorMsg = error.message || 'Failed to upload media';
      toast.error(errorMsg);
      onUploadError?.(new Error(errorMsg));
      setPreviewUrl(null);
      setFileName('');
      setUploading(false);
      setUploadProgress(0);
      setUploadComplete(false);
    }
  };

  const handleRemoveConfirmed = () => {
    setPreviewUrl(null);
    setFileName('');
    if (audioElement) {
      audioElement.pause();
      setAudioElement(null);
    }
    onRemove?.();
    setShowDeleteConfirm(false);
  };

  const toggleAudioPlayback = () => {
    if (!audioElement && currentUrl) {
      const audio = new Audio(currentUrl);
      audio.addEventListener('ended', () => setIsAudioPlaying(false));
      audio.play();
      setAudioElement(audio);
      setIsAudioPlaying(true);
    } else if (audioElement) {
      if (isAudioPlaying) {
        audioElement.pause();
        setIsAudioPlaying(false);
      } else {
        audioElement.play();
        setIsAudioPlaying(true);
      }
    }
  };

  const displayFile = previewUrl || currentUrl;
  const isImage = displayFile && (displayFile.match(/\.(jpg|jpeg|png|gif|webp)$/i) || previewUrl?.startsWith('blob:'));
  const isAudio = displayFile && displayFile.match(/\.(mp3|wav|ogg|m4a)$/i);
  const isVideo = displayFile && displayFile.match(/\.(mp4|webm|mov)$/i);

  const getFileTypeLabel = () => {
    switch (mediaType) {
      case 'image':
        return 'PNG, JPG, GIF, WebP';
      case 'audio':
        return 'MP3, WAV';
      case 'video':
        return 'MP4, WebM';
      default:
        return 'Any media type';
    }
  };

  return (
    <FormField label={label} description={description} error={error} required={required} className={className}>
      <div className="space-y-3">
        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-xl transition-all duration-200 ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : displayFile
              ? 'border-gray-200 bg-gray-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
          }`}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {displayFile && isImage ? (
            // Image Preview
            <div className="relative group p-6">
              <div className="relative max-w-full mx-auto">
                <img
                  src={displayFile}
                  alt="Preview"
                  className="w-full max-w-md h-auto max-h-64 object-contain rounded-lg border border-gray-300 bg-white shadow-sm mx-auto"
                />

                {/* Upload Progress Overlay */}
                {uploading && (
                  <div className="absolute inset-0 bg-white/98 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <div className="w-full max-w-xs px-6">
                      {uploadComplete ? (
                        <div className="text-center space-y-3">
                          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <CheckCircle2 className="w-7 h-7 text-green-600" />
                          </div>
                          <p className="text-sm font-semibold text-green-700">Upload complete!</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="w-12 h-12 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs font-medium text-gray-700">
                              <span className="truncate max-w-[180px]">{fileName}</span>
                              <span className="text-blue-600">{Math.round(uploadProgress)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Hover Actions */}
                {!uploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex gap-3">
                      <TooltipArrow content="View full size" position="top">
                        <button
                          type="button"
                          onClick={() => setShowPreviewModal(true)}
                          className="p-3 bg-white rounded-xl shadow-lg hover:bg-gray-50 transition-colors"
                        >
                          <Eye className="w-5 h-5 text-gray-700" />
                        </button>
                      </TooltipArrow>
                      <TooltipArrow content="Remove file" position="top">
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="p-3 bg-white rounded-xl shadow-lg hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </TooltipArrow>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : displayFile && isAudio ? (
            // Audio Player
            <div className="p-6">
              <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={toggleAudioPlayback}
                    className="w-12 h-12 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                  >
                    {isAudioPlaying ? (
                      <Pause className="w-5 h-5 text-white" />
                    ) : (
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Volume2 className="w-4 h-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-900">Audio File</p>
                    </div>
                    <p className="text-xs text-gray-500 truncate">{fileName || 'audio.mp3'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ) : displayFile && isVideo ? (
            // Video Preview
            <div className="relative group p-6">
              <div className="relative max-w-full mx-auto">
                <video
                  src={displayFile}
                  controls
                  controlsList="nodownload"
                  disablePictureInPicture
                  className="w-full max-w-2xl h-auto max-h-80 rounded-lg border border-gray-300 bg-black shadow-sm mx-auto"
                >
                  Your browser does not support the video tag.
                </video>

                {/* Hover Actions */}
                {!uploading && (
                  <div className="absolute top-2 right-2">
                    <TooltipArrow content="Remove video" position="left">
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="p-2 bg-white/90 rounded-lg shadow-lg hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </TooltipArrow>
                  </div>
                )}
              </div>
            </div>
          ) : displayFile ? (
            // Generic file preview
            <div className="p-6 text-center">
              <div className="text-gray-600 mb-2">
                <Check className="w-8 h-8 mx-auto" />
              </div>
              <p className="text-sm font-medium text-gray-700">{fileName || 'File uploaded'}</p>
            </div>
          ) : (
            // Upload Placeholder
            <div className="p-8 text-center">
              <div
                className={`mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-all duration-200 ${
                  isDragging ? 'bg-blue-500 text-white scale-105' : 'bg-gray-100 text-gray-400'
                }`}
              >
                <Upload className="w-6 h-6" />
              </div>

              <div className="space-y-1">
                <p className={`font-medium transition-colors ${isDragging ? 'text-blue-600' : 'text-gray-900'}`}>
                  {isDragging ? 'Drop to upload' : 'Upload media'}
                </p>
                <p className="text-sm text-gray-500">
                  Drag & drop or{' '}
                  <span className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer">browse</span>
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {getFileTypeLabel()} • Max {maxSize}MB
                </p>
              </div>
            </div>
          )}

          {/* Hidden File Input */}
          {!uploading && (
            <input
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          )}
        </div>

        {/* Action Buttons */}
        {displayFile && !uploading && (
          <div className="flex gap-2">
            <TooltipArrow content="Upload a new file" position="top">
              <label className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors cursor-pointer">
                <Upload className="w-4 h-4" />
                Change File
                <input type="file" accept={accept} onChange={handleFileSelect} className="hidden" />
              </label>
            </TooltipArrow>
            {isImage && (
              <TooltipArrow content="View full size" position="top">
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(true)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </TooltipArrow>
            )}
            <TooltipArrow content="Remove file" position="top">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </TooltipArrow>
          </div>
        )}
      </div>

      {/* Full Size Preview Modal */}
      {showPreviewModal && displayFile && isImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              onClick={() => setShowPreviewModal(false)}
              className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img src={displayFile} alt="Full size preview" className="max-w-full max-h-full object-contain rounded-lg" />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleRemoveConfirmed}
        title="Delete Media"
        description="Are you sure you want to delete this media file? This action cannot be undone."
        itemName={fileName || 'media file'}
        isLoading={false}
      />
    </FormField>
  );
}
